import { useCallback, useEffect, useMemo, useState } from 'react';
import { projectId } from '../utils/supabase/info';
import type { SkipDecision, SkipPlan } from '../types/skipPlan';

export type SkipPlanScope = { kind: 'item'; itemId: string } | { kind: 'category'; category: string };

export interface SkipPlanQuestionInfo {
  id: string;
  text: string;
  type?: string;
}

export interface SkipPlanResponse extends SkipPlan {
  questions?: SkipPlanQuestionInfo[];
}

export interface AcceptedAnswer {
  questionId: string;
  answer: unknown;
  confidence?: number;
  evidence?: SkipDecision['evidence'];
  action?: 'skip' | 'suggest';
}

export interface UseSkipPlanArgs {
  companyId: string | null | undefined;
  accessToken: string | null | undefined;
  scope: SkipPlanScope;
  engagementId?: string | null;
  /** Set false to hold off (e.g. until questions have loaded) */
  enabled?: boolean;
}

export interface UseSkipPlanResult {
  plan: SkipPlanResponse | null;
  loading: boolean;
  applying: boolean;
  error: string | null;
  /** AI decisions the user may accept (skip + suggest with an answer) */
  acceptable: SkipDecision[];
  refresh: (opts?: { force?: boolean }) => Promise<void>;
  apply: (accepted: AcceptedAnswer[]) => Promise<boolean>;
}

function base(): string {
  return `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/discovery`;
}

export function skipPlanUrl(companyId: string, scope: SkipPlanScope): string {
  return scope.kind === 'item'
    ? `${base()}/${encodeURIComponent(companyId)}/${encodeURIComponent(scope.itemId)}/skip-plan`
    : `${base()}/${encodeURIComponent(companyId)}/category/${encodeURIComponent(scope.category)}/skip-plan`;
}

export function toAccepted(decisions: ReadonlyArray<SkipDecision>): AcceptedAnswer[] {
  return decisions
    .filter((d) => d.decidedBy === 'ai' && (d.action === 'skip' || d.action === 'suggest') && d.answer !== undefined)
    .map((d) => ({ questionId: d.questionId, answer: d.answer, confidence: d.confidence, evidence: d.evidence, action: d.action as 'skip' | 'suggest' }));
}

/**
 * Loads the AI skip plan for a config item or guided category and applies
 * accepted answers. Never throws; failures surface as `error` and a null plan
 * so callers simply show every question.
 */
export function useSkipPlan({ companyId, accessToken, scope, engagementId, enabled = true }: UseSkipPlanArgs): UseSkipPlanResult {
  const [plan, setPlan] = useState<SkipPlanResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scopeKey = scope.kind === 'item' ? `item:${scope.itemId}` : `category:${scope.category}`;

  const refresh = useCallback(
    async (opts: { force?: boolean } = {}) => {
      if (!companyId || !accessToken || !enabled) return;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (engagementId) params.set('engagementId', engagementId);
        if (opts.force) params.set('refresh', '1');
        const qs = params.toString();
        const res = await fetch(`${skipPlanUrl(companyId, scope)}${qs ? `?${qs}` : ''}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) throw new Error(`skip-plan ${res.status}`);
        setPlan((await res.json()) as SkipPlanResponse);
      } catch (e: any) {
        console.warn('[useSkipPlan] load failed; showing all questions', e);
        setPlan(null);
        setError(e?.message ?? 'Failed to load');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId, accessToken, scopeKey, engagementId, enabled],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const apply = useCallback(
    async (accepted: AcceptedAnswer[]): Promise<boolean> => {
      if (!companyId || !accessToken || accepted.length === 0) return false;
      setApplying(true);
      try {
        const res = await fetch(`${skipPlanUrl(companyId, scope)}/apply`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ accepted }),
        });
        if (!res.ok) throw new Error(`apply ${res.status}`);
        // Applied answers are now "already answered": drop them from the plan locally.
        setPlan((prev) => {
          if (!prev) return prev;
          const done = new Set(accepted.map((a) => a.questionId));
          return {
            ...prev,
            decisions: prev.decisions.map((d) =>
              done.has(d.questionId) ? { ...d, action: 'skip', reason: 'already_answered', decidedBy: 'rules', confidence: 1 } : d,
            ),
          };
        });
        return true;
      } catch (e: any) {
        console.warn('[useSkipPlan] apply failed', e);
        setError(e?.message ?? 'Failed to apply');
        return false;
      } finally {
        setApplying(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId, accessToken, scopeKey],
  );

  const acceptable = useMemo(() => (plan ? plan.decisions.filter((d) => d.decidedBy === 'ai' && (d.action === 'skip' || d.action === 'suggest') && d.answer !== undefined) : []), [plan]);

  return { plan, loading, applying, error, acceptable, refresh, apply };
}
